import { Avatar, Box, Grid, Radio, Typography } from "@mui/material";
import { styled } from "@mui/system";
import PForPeep from "assets/icons/PForPeep";
import { getImageSrc } from "helpers/getImageSrc";
import { useTranslation } from "react-i18next";
import { useSelector } from "store";
import { selectOutletById } from "store/selectors";

type OutletModalItemProps = {
  outletId: number;
  selectedOutletId: Nullable<number>;
  onClick: (outletId: number) => void;
  disabled?: boolean;
  disabledText?: string;
};

const OutletModalItem = ({
  outletId,
  onClick,
  selectedOutletId,
  disabled = false,
  disabledText = "",
}: OutletModalItemProps) => {
  const { t } = useTranslation("outletPage");

  const outlet = useSelector(selectOutletById(outletId));

  const outletImage = getImageSrc(outlet?.primaryImage) || "";

  if (!outlet) return null;

  const outletName = outlet?.name || "";

  return (
    <OutletModalItemWrapper
      disabled={disabled}
      sx={{ display: outlet ? "block" : "none" }}
      onClick={() => (disabled ? {} : onClick(outlet?.id))}
    >
      <Grid container>
        <Grid item xs={8} display="flex" alignItems="center">
          <OutletAvatarWrapper>
            {outlet?.name && (
              <Avatar alt={outlet?.name} src={outletImage} sx={{ width: 40, height: 40 }}>
                <PForPeep fontSize="large" />
              </Avatar>
            )}
          </OutletAvatarWrapper>

          <Box>
            <Typography
              variant="primary"
              fontWeight="700"
              color={!outlet.isAcceptingOnlineBookings ? "#848FA1" : "text.label"}
              component="h4"
              display="inline"
            >
              {outletName}
            </Typography>

            {!outlet.isAcceptingOnlineBookings && (
              <Box
                sx={{
                  background: "#FFEED1",
                  color: "#FF9800",
                  display: "inline-block",
                  padding: "0.25rem 1rem",
                  borderRadius: 1,
                }}
                ml={1}
                fontWeight={700}
              >
                {t("busy")}
              </Box>
            )}

            {disabled && disabledText && (
              <Typography variant="secondary" fontWeight="500" color="text.primary" component="p">
                {disabledText}
              </Typography>
            )}
          </Box>
        </Grid>

        <Grid item xs={4} textAlign="right">
          <Radio disabled={disabled} checked={selectedOutletId === outlet?.id} />
        </Grid>
      </Grid>
    </OutletModalItemWrapper>
  );
};

export default OutletModalItem;

type OutletModalItemWrapperProps = {
  disabled: boolean;
};

const OutletModalItemWrapper = styled(Box)<OutletModalItemWrapperProps>(({ theme, disabled }) => ({
  background: disabled ? theme.palette.bg.disabled : null,
  border: `1px solid ${theme.palette.common.gray}24`,
  borderRadius: 4,
  minHeight: "64px",
  padding: theme.spacing(1.5),
  marginBottom: theme.spacing(1.5),
  display: "flex",
  alignItems: "center",
  userSelect: "none",
  cursor: "pointer",
}));

const OutletAvatarWrapper = styled(Box)(({ theme }) => ({
  marginRight: theme.spacing(1.5),
  ".MuiSvgIcon-root": {
    width: 31,
    height: 31,
  },
}));
